import { createLogger } from '@/lib/logger'

const logger = createLogger('lib/prefetch/modulePrefetch')

export type PrefetchEntry = {
  id: string
  hrefs: string[]
  load: () => Promise<unknown>
}

export const coreModulePrefetchEntries: PrefetchEntry[] = [
  { id: 'today', hrefs: ['/today'], load: () => import('@/pages/today') },
  {
    id: 'tasks',
    hrefs: ['/tasks', '/tasks/mine'],
    load: () => import('@/pages/tasks'),
  },
  {
    id: 'notes-mine',
    hrefs: ['/notes/mine'],
    load: () => import('@/pages/my-notes'),
  },
  { id: 'my-time', hrefs: ['/my-time'], load: () => import('@/pages/my-time') },
  {
    id: 'threads',
    hrefs: ['/threads'],
    load: () => import('@/pages/discussions/page'),
  },
  {
    id: 'calendar',
    hrefs: ['/calendar'],
    load: () => import('@/pages/calendar'),
  },
  {
    id: 'projects',
    hrefs: ['/projects'],
    load: () => import('@/pages/projects'),
  },
  { id: 'notes', hrefs: ['/notes'], load: () => import('@/pages/notes') },
]

export const secondaryModulePrefetchEntries: PrefetchEntry[] = [
  {
    id: 'entities',
    hrefs: ['/entities'],
    load: () => import('@/pages/entities'),
  },
  {
    id: 'collections',
    hrefs: ['/collections'],
    load: () => import('@/pages/collections'),
  },
  { id: 'forms', hrefs: ['/forms'], load: () => import('@/pages/forms') },
  {
    id: 'documents',
    hrefs: ['/documents'],
    load: () => import('@/pages/documents'),
  },
  {
    id: 'timesheets',
    hrefs: ['/timesheets'],
    load: () => import('@/pages/timesheets'),
  },
  { id: 'deals', hrefs: ['/deals'], load: () => import('@/pages/deals') },
  {
    id: 'contacts',
    hrefs: ['/contacts'],
    load: () => import('@/pages/contacts'),
  },
  { id: 'team', hrefs: ['/team'], load: () => import('@/pages/team') },
  {
    id: 'products',
    hrefs: ['/products'],
    load: () => import('@/pages/products'),
  },
  {
    id: 'contracts',
    hrefs: ['/contracts'],
    load: () => import('@/pages/contracts/page'),
  },
  {
    id: 'invoices',
    hrefs: ['/invoices'],
    load: () => import('@/pages/invoices'),
  },
  { id: 'events', hrefs: ['/events'], load: () => import('@/pages/events') },
  {
    id: 'workflows',
    hrefs: ['/workflows'],
    load: () => import('@/pages/workflows'),
  },
  {
    id: 'analytics',
    hrefs: ['/analytics'],
    load: () => import('@/pages/analytics'),
  },
  {
    id: 'admin-members',
    hrefs: ['/admin/members', '/settings/administration/members'],
    load: () => import('@/pages/admin/members'),
  },
  {
    id: 'admin-integrations',
    hrefs: ['/admin/integrations', '/settings/administration/integrations'],
    load: () => import('@/pages/admin/integrations'),
  },
  {
    id: 'admin-settings',
    hrefs: ['/admin/settings', '/settings'],
    load: () => import('@/pages/admin/settings'),
  },
  {
    id: 'admin-subscription',
    hrefs: ['/admin/subscription', '/settings/administration/subscription'],
    load: () => import('@/pages/admin/subscription'),
  },
]

/**
 * Module-level UI chunks that are still split with React.lazy inside module pages.
 * Preloading these in Tauri removes first-open spinners for tab/panel UI within modules.
 */
export const moduleUiLazyPrefetchEntries: PrefetchEntry[] = [
  // Projects: detail tabs loaded lazily in project-detail-tabs/index.tsx
  {
    id: 'project-tab-overview',
    hrefs: [],
    load: () => import('@/pages/projects/project-detail-tabs/overview'),
  },
  {
    id: 'project-tab-tasks',
    hrefs: [],
    load: () => import('@/pages/projects/project-detail-tabs/tasks'),
  },
  {
    id: 'project-tab-board',
    hrefs: [],
    load: () => import('@/pages/projects/project-detail-tabs/board'),
  },
  {
    id: 'project-tab-timeline',
    hrefs: [],
    load: () => import('@/pages/projects/project-detail-tabs/timeline'),
  },
  {
    id: 'project-tab-settings',
    hrefs: [],
    load: () => import('@/pages/projects/project-detail-tabs/settings'),
  },
  {
    id: 'project-tab-documents',
    hrefs: [],
    load: () => import('@/pages/projects/project-detail-tabs/documents'),
  },
  {
    id: 'project-tab-discussions',
    hrefs: [],
    load: () => import('@/pages/projects/project-detail-tabs/discussions'),
  },

  // Contracts: settings tab is lazy-loaded
  {
    id: 'contracts-settings-tab',
    hrefs: [],
    load: () => import('@/pages/contracts/tabs/settings-tab'),
  },

  // Admin: members external org tab is lazy-loaded
  {
    id: 'admin-external-orgs-tab',
    hrefs: [],
    load: () => import('@/pages/admin/members/external-organizations'),
  },

  // Tasks: inline detail lazy chunks
  {
    id: 'tasks-subtasks-section',
    hrefs: [],
    load: () => import('@/components/tasks/_components/SubtasksSection'),
  },
  {
    id: 'tasks-mini-document-list',
    hrefs: [],
    load: () => import('@/components/shared/documents/MiniDocumentList'),
  },
  {
    id: 'tasks-mini-document-uploader',
    hrefs: [],
    load: () => import('@/components/shared/documents/MiniDocumentUploader'),
  },
  {
    id: 'tasks-notes-section',
    hrefs: [],
    load: () => import('@/components/tasks/task-sheet/TaskNotesSection'),
  },
]

const prefetchIndex = new Map<string, PrefetchEntry>()
const prefetched = new Set<string>()
const inFlight = new Map<string, Promise<void>>()

for (const entry of [
  ...coreModulePrefetchEntries,
  ...secondaryModulePrefetchEntries,
]) {
  for (const href of entry.hrefs) {
    prefetchIndex.set(href, entry)
  }
}

export const prefetchModuleForPath = async (path: string): Promise<void> => {
  const pathname = path.split(/[?#]/)[0] ?? path
  const entry =
    prefetchIndex.get(pathname) ??
    [...prefetchIndex.entries()]
      .sort(([a], [b]) => b.length - a.length)
      .find(
        ([href]) => pathname.endsWith(href) || pathname.includes(`${href}/`),
      )?.[1]
  if (!entry || prefetched.has(entry.id)) {
    return
  }

  const existing = inFlight.get(entry.id)
  if (existing) {
    await existing
    return
  }

  const loadPromise = entry
    .load()
    .then(() => {
      prefetched.add(entry.id)
    })
    .catch(error => {
      logger.debug(`Prefetch failed for ${entry.id}:`, error)
    })
    .finally(() => {
      inFlight.delete(entry.id)
    })

  inFlight.set(entry.id, loadPromise)
  await loadPromise
}
